import React from 'react';
import { DollarSign, TrendingUp, TrendingDown, Calendar } from 'lucide-react';

const TotalSpendOverview = ({ transactions, dateRangeLabel }) => {
  // Calculate total spend
  const totalSpend = transactions.reduce((sum, txn) => sum + parseFloat(txn.amount || 0), 0);

  // Calculate number of distinct days
  const distinctDates = new Set(
    transactions.map(txn => new Date(txn.transactionDate).toDateString())
  );
  const numberOfDays = Math.max(distinctDates.size, 1);

  // Calculate average daily spend
  const avgDailySpend = totalSpend / numberOfDays;

  // Transaction count
  const transactionCount = transactions.length;

  // Calculate month-over-month change
  const calculateMoMChange = () => {
    if (transactions.length === 0) return { percentChange: 0, isIncrease: false };

    // Sort transactions by date
    const sortedTxns = [...transactions].sort(
      (a, b) => new Date(a.transactionDate) - new Date(b.transactionDate)
    );

    if (sortedTxns.length === 0) return { percentChange: 0, isIncrease: false };

    const latestDate = new Date(sortedTxns[sortedTxns.length - 1].transactionDate);
    const earliestDate = new Date(sortedTxns[0].transactionDate);

    // Calculate the period length in days
    const periodLengthMs = latestDate - earliestDate;
    const periodLengthDays = Math.max(periodLengthMs / (1000 * 60 * 60 * 24), 1);

    // Split into current and previous periods
    const midpoint = new Date(earliestDate.getTime() + periodLengthMs / 2);

    const currentPeriod = sortedTxns.filter(txn => new Date(txn.transactionDate) >= midpoint);
    const previousPeriod = sortedTxns.filter(txn => new Date(txn.transactionDate) < midpoint);

    const currentSpend = currentPeriod.reduce((sum, txn) => sum + parseFloat(txn.amount || 0), 0);
    const previousSpend = previousPeriod.reduce((sum, txn) => sum + parseFloat(txn.amount || 0), 0);

    if (previousSpend === 0) {
      return { percentChange: currentSpend > 0 ? 100 : 0, isIncrease: currentSpend > 0 };
    }

    const percentChange = ((currentSpend - previousSpend) / previousSpend) * 100;
    return { percentChange, isIncrease: percentChange > 0 };
  };

  const { percentChange, isIncrease } = calculateMoMChange();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {/* Total Spend Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600">Total Spend</h3>
          <div className="bg-primary-100 p-2 rounded-lg">
            <DollarSign className="h-5 w-5 text-primary-600" />
          </div>
        </div>
        <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalSpend)}</p>
        <p className="text-sm text-gray-500 mt-1">{transactionCount} transactions</p>
      </div>

      {/* Average Daily Spend Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600">Avg Daily Spend</h3>
          <div className="bg-blue-100 p-2 rounded-lg">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
        </div>
        <p className="text-3xl font-bold text-gray-900">{formatCurrency(avgDailySpend)}</p>
        <p className="text-sm text-gray-500 mt-1">Over {numberOfDays} days</p>
      </div>

      {/* Period Change Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600">Period Change</h3>
          <div className={`p-2 rounded-lg ${isIncrease ? 'bg-red-100' : 'bg-green-100'}`}>
            {isIncrease ? (
              <TrendingUp className="h-5 w-5 text-red-600" />
            ) : (
              <TrendingDown className="h-5 w-5 text-green-600" />
            )}
          </div>
        </div>
        <p className={`text-3xl font-bold ${isIncrease ? 'text-red-600' : 'text-green-600'}`}>
          {isIncrease ? '+' : ''}{percentChange.toFixed(1)}%
        </p>
        <p className="text-sm text-gray-500 mt-1">vs previous period</p>
      </div>
    </div>
  );
};

export default TotalSpendOverview;
